/**
 * Renders the "X won!" result card that the winner popup offers for sharing.
 *
 * Canvas rather than a screenshot library: html2canvas and friends are ~50 kB
 * gzip and re-implement CSS layout badly. The card is six shapes and two lines
 * of text, so drawing it directly is smaller, faster, and produces the same
 * image on every browser instead of one that depends on the visitor's fonts.
 *
 * 1200x630 matches the Open Graph aspect ratio, so the image is not re-cropped
 * when it is posted to X, Facebook, LinkedIn, or WhatsApp.
 */

const W = 1200;
const H = 630;
const VIOLET = '#7c3aed';
const VIOLET_DARK = '#5b21b6';

export interface WinnerCardCopy {
  resultLabel: string;
  attribution: string;
  shareText: string;
  shareTitle: string;
}

function defaultCopy(winner: string, wheelTitle: string): WinnerCardCopy {
  return {
    resultLabel: 'won the spin',
    attribution: 'Picked at random on thenamewheel.com',
    shareText: `${winner} won "${wheelTitle}" — picked at random on thenamewheel.com`,
    shareTitle: `${winner} won!`,
  };
}

/** Shrink the font until the name fits, rather than truncating someone's name. */
function fitText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, startPx: number): number {
  let px = startPx;
  for (;;) {
    ctx.font = `900 ${px}px 'Inter Variable', Inter, system-ui, sans-serif`;
    if (ctx.measureText(text).width <= maxWidth || px <= 28) return px;
    px -= 4;
  }
}

/**
 * Draw the card. Returns null when canvas is unavailable (SSR, or a browser
 * with canvas disabled) so callers can hide the button rather than fail.
 */
export function drawWinnerCard(
  winner: string,
  wheelTitle: string,
  copy: WinnerCardCopy = defaultCopy(winner, wheelTitle),
): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, VIOLET);
  bg.addColorStop(1, VIOLET_DARK);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Faint wheel mark, bled off the right edge.
  ctx.save();
  ctx.globalAlpha = 0.08;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 14;
  ctx.beginPath();
  ctx.arc(W - 90, H / 2, 240, 0, 2 * Math.PI);
  ctx.stroke();
  for (let i = 0; i < 8; i++) {
    const a = (i * Math.PI) / 4;
    ctx.beginPath();
    ctx.moveTo(W - 90, H / 2);
    ctx.lineTo(W - 90 + 240 * Math.cos(a), H / 2 + 240 * Math.sin(a));
    ctx.stroke();
  }
  ctx.restore();

  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';

  // Wheel title, so a shared card says which draw it came from.
  ctx.fillStyle = 'rgba(255,255,255,0.72)';
  ctx.font = "700 30px 'Inter Variable', Inter, system-ui, sans-serif";
  const title = wheelTitle.length > 42 ? `${wheelTitle.slice(0, 41)}…` : wheelTitle;
  ctx.fillText(title.toUpperCase(), 80, 150);

  // The winner.
  const px = fitText(ctx, winner, W - 300, 118);
  ctx.fillStyle = '#ffffff';
  ctx.font = `900 ${px}px 'Inter Variable', Inter, system-ui, sans-serif`;
  ctx.fillText(winner, 80, 340);

  ctx.fillStyle = 'rgba(255,255,255,0.92)';
  ctx.font = "700 40px 'Inter Variable', Inter, system-ui, sans-serif";
  ctx.fillText(copy.resultLabel, 80, 410);

  ctx.fillStyle = 'rgba(255,255,255,0.65)';
  ctx.font = "600 26px 'Inter Variable', Inter, system-ui, sans-serif";
  ctx.fillText(copy.attribution, 80, 540);

  return canvas;
}

export function winnerCardBlob(winner: string, wheelTitle: string, copy?: WinnerCardCopy): Promise<Blob | null> {
  const canvas = drawWinnerCard(winner, wheelTitle, copy);
  if (!canvas) return Promise.resolve(null);
  return new Promise(resolve => canvas.toBlob(b => resolve(b), 'image/png'));
}

/** Filename-safe slug for the download. */
function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'winner';
}

export function downloadWinnerCard(winner: string, wheelTitle: string, copy?: WinnerCardCopy): void {
  const canvas = drawWinnerCard(winner, wheelTitle, copy);
  if (!canvas) return;
  const a = document.createElement('a');
  a.href = canvas.toDataURL('image/png');
  a.download = `${slug(winner)}-won.png`;
  a.click();
}

export function shareText(winner: string, wheelTitle: string, copy?: WinnerCardCopy): string {
  return copy?.shareText ?? defaultCopy(winner, wheelTitle).shareText;
}

/**
 * Share the card through the OS share sheet where that is available, falling
 * back to a PNG download.
 *
 * `canShare({ files })` is checked rather than assumed: Web Share Level 2 is
 * absent on desktop Firefox and older Safari, where calling share() with files
 * rejects rather than degrading.
 */
export async function shareWinner(
  winner: string,
  wheelTitle: string,
  copy?: WinnerCardCopy,
): Promise<'shared' | 'downloaded'> {
  const resolvedCopy = copy ?? defaultCopy(winner, wheelTitle);
  const text = resolvedCopy.shareText;
  const blob = await winnerCardBlob(winner, wheelTitle, resolvedCopy);
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };

  if (blob && typeof navigator.share === 'function' && nav.canShare) {
    const file = new File([blob], `${slug(winner)}-won.png`, { type: 'image/png' });
    if (nav.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], text, title: resolvedCopy.shareTitle });
        return 'shared';
      } catch (err) {
        // A user dismissing the sheet throws AbortError — that is a completed
        // interaction, not a failure, so it must not fall through to a download
        // the visitor did not ask for.
        if (err instanceof DOMException && err.name === 'AbortError') return 'shared';
      }
    }
  }

  downloadWinnerCard(winner, wheelTitle, resolvedCopy);
  return 'downloaded';
}

/** X/Twitter intent URL — text only; the image has to be attached by the user. */
export function tweetUrl(winner: string, wheelTitle: string, copy?: WinnerCardCopy): string {
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText(winner, wheelTitle, copy))}`;
}
